/**
 * <QuizQuestionSignalSheet> — signaler une question du quiz de prérequis (organism).
 *
 * L'ancienne boîte de `QuizQuestionSignalButton` : « Signaler cette question », « Quel est le
 * problème avec cette question ? », les quatre problèmes (une seule réponse), un commentaire
 * facultatif, et « Envoyer », grisé tant qu'aucun problème n'est choisi. Un envoi refusé se dit dans
 * la boîte, le choix et le commentaire gardés.
 *
 * Contrôlée : le choix, le commentaire et l'envoi arrivent en props ; chaque geste remonte.
 */
import React from "react"

import { Banner } from "../../atoms/Banner"
import { Button } from "../../atoms/Buttons/Button"
import { Stack } from "../../atoms/containers/Stack"
import { TextInput } from "../../atoms/TextInput"
import { Text } from "../../atoms/typography/Text"
import { Modal } from "../../molecules/Modal"
import { OptionCard } from "../../molecules/OptionCard"

/** Les quatre problèmes de l'ancien code, dans son ordre (`quizSignalConstants`). */
export type QuizSignalKind = "incorrect_question" | "incorrect_answer" | "display_issue" | "too_difficult"

export type QuizQuestionSignalSheetProps = {
    visible: boolean
    type: QuizSignalKind | null
    comment: string
    sending?: boolean
    /** L'envoi a été refusé. */
    failed?: boolean
    onType: (type: QuizSignalKind) => void
    onComment: (comment: string) => void
    onSend: () => void
    onClose: () => void
    testID?: string
}

const KINDS: { kind: QuizSignalKind; label: string }[] = [
    { kind: "incorrect_question", label: "Question incorrecte ou pas claire" },
    { kind: "incorrect_answer", label: "Réponse(s) incorrecte(s)" },
    { kind: "display_issue", label: "Problème d'affichage" },
    { kind: "too_difficult", label: "Trop difficile ou pas adapté au niveau" },
]

const TEXTS = {
    title: "Signaler cette question",
    question: "Quel est le problème avec cette question ?",
    comment: "Commentaire (facultatif)",
    send: "Envoyer",
    failed: "Le signalement n'est pas parti. Réessaie dans un instant.",
}

export function QuizQuestionSignalSheet(props: QuizQuestionSignalSheetProps) {
    const {
        visible,
        type,
        comment,
        sending = false,
        failed = false,
        onType,
        onComment,
        onSend,
        onClose,
        testID,
    } = props
    return (
        <Modal
            visible={visible}
            onClose={onClose}
            title={TEXTS.title}
            dismissable={!sending}
            sizeToContent
            footer={<Button label={TEXTS.send} onPress={onSend} loading={sending} disabled={type == null} fullWidth />}
            testID={testID}
        >
            <Stack gap="4">
                <Text color="text-muted">{TEXTS.question}</Text>
                <Stack gap="2">
                    {KINDS.map(({ kind, label }) => (
                        <OptionCard
                            key={kind}
                            label={label}
                            selected={type === kind}
                            disabled={sending}
                            onPress={() => onType(kind)}
                        />
                    ))}
                </Stack>
                <TextInput
                    value={comment}
                    onChangeText={onComment}
                    placeholder={TEXTS.comment}
                    multiline
                    numberOfLines={3}
                    disabled={sending}
                />
                {failed && <Banner color="danger" tinted icon="exclamation-circle" message={TEXTS.failed} />}
            </Stack>
        </Modal>
    )
}
